/**
 * AdSlot — pre-sized ad placeholder with switchable provider support.
 *
 * Provider is selected at build time via REACT_APP_AD_PROVIDER:
 *   - 'adsense'  (default when unset) — Google AdSense manual slots
 *   - 'medianet'                      — Media.net manual slots
 *   - 'none'                          — No ads; placeholder only (staging / PR preview use)
 *
 * TC-014 / design spec:
 *  - Desktop: 728×90 leaderboard, below the filter bar.
 *  - Mobile: 300×250 rectangle, above the results list.
 *  - Space is pre-allocated via min-height to eliminate CLS (NFR).
 *
 * AdSense integration (REACT_APP_AD_PROVIDER=adsense or unset):
 *  - Requires REACT_APP_AD_PUBLISHER_ID and a per-slot slotId prop.
 *  - When both are set:
 *      - Injects <script async src="pagead2.googlesyndication.com/..."> into <head>
 *        exactly once (deduplicated via data-adsense-script sentinel attribute).
 *      - Renders an <ins class="adsbygoogle"> inside the slot container.
 *      - Calls (window.adsbygoogle = window.adsbygoogle || []).push({}) once per slot.
 *  - When either is unset: pre-sized placeholder only (no script, no <ins>).
 *
 * Media.net integration (REACT_APP_AD_PROVIDER=medianet):
 *  - Requires REACT_APP_MEDIANET_SITE_ID and a per-slot slotId prop.
 *  - When both are set:
 *      - Injects <script async src="//contextual.media.net/dmedianet.js?cid=..."> into
 *        <head> exactly once (deduplicated via data-medianet-script sentinel attribute).
 *      - Renders a <div id="mn-slot-{variant}-{slotId}"> inside the slot container.
 *      - Calls window._mNDetails.loadTag(divId, slotId, siteId) once per mounted slot.
 *  - When siteId or slotId is unset: pre-sized placeholder only.
 *
 * none (REACT_APP_AD_PROVIDER=none):
 *  - No script tags injected. No <ins> or slot <div> rendered.
 *  - Slot container preserved in DOM (pre-sized) to maintain layout stability (FR-036).
 *
 * Graceful degradation:
 *  - Script load errors are silent — the slot container stays in pre-allocated state (FR-036).
 *  - loadTag failures are caught and silenced — layout is never disrupted.
 *
 * FTC disclosure: "Advertisement" visible text node (FR-035).
 * Accessibility: aria-label + role="complementary" meet WCAG 2.1 AA (TC-009, AC-11).
 *
 * Work item 08 status: complete (AdSense integration wired 2026-04-24).
 * Work item 53 status: complete (provider abstraction 2026-05-13).
 */

import React, { useEffect, useRef } from 'react';

// ── Window type extensions ────────────────────────────────────────────────────

declare global {
  interface Window {
    /** AdSense expects a pushable array of config objects. */
    adsbygoogle: object[];
    /** Media.net per-slot initialization API. */
    _mNDetails: {
      loadTag: (divId: string, slotId: string, siteId: string) => void;
    };
  }
}

// ── Build-time provider selection ─────────────────────────────────────────────

/**
 * Resolved ad provider type. CRA inlines REACT_APP_* references at build time —
 * the value of process.env.REACT_APP_AD_PROVIDER is a build-time constant
 * regardless of where in the source it is read. Reading it inline (rather than
 * at module scope) preserves the same production behaviour while allowing the
 * Jest test environment to control it via process.env mutation between tests.
 *
 * Switching providers requires only a redeploy (no code change).
 */
type AdProvider = 'adsense' | 'medianet' | 'none';

/** Returns the active ad provider, defaulting to 'adsense' when unset. */
function getProvider(): AdProvider {
  return (process.env.REACT_APP_AD_PROVIDER ?? 'adsense') as AdProvider;
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Sentinel attribute used to deduplicate the AdSense global script in <head>. */
const ADSENSE_SCRIPT_ATTR = 'data-adsense-script';

/** Sentinel attribute used to deduplicate the Media.net global script in <head>. */
const MEDIANET_SCRIPT_ATTR = 'data-medianet-script';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  variant: 'leaderboard' | 'rectangle';
  /**
   * Per-slot identifier passed in by the consuming page from its own env var.
   *   - AdSense:    AdSense ad-unit ID (e.g. "1234567890")
   *   - Media.net:  data-slot value (e.g. "987654321")
   * When omitted the slot renders the pre-sized placeholder only.
   */
  slotId?: string;
}

const SIZES: Record<Props['variant'], { width: number; height: number }> = {
  leaderboard: { width: 728, height: 90 },
  rectangle: { width: 300, height: 250 },
};

// ── Loader helpers ────────────────────────────────────────────────────────────

/**
 * Injects the AdSense global loader script into <head> exactly once.
 * Idempotent: subsequent calls while the sentinel attribute is present are no-ops.
 */
function loadAdsenseLoader(publisherId: string): void {
  if (document.querySelector(`script[${ADSENSE_SCRIPT_ATTR}]`)) return;

  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${publisherId}`;
  script.setAttribute(ADSENSE_SCRIPT_ATTR, 'true');
  // Graceful failure: silent on error so layout is never disrupted (FR-036).
  script.onerror = () => {};
  document.head.appendChild(script);
}

/**
 * Injects the Media.net global loader script into <head> exactly once.
 * Idempotent: subsequent calls while the sentinel attribute is present are no-ops.
 */
function loadMedianetLoader(siteId: string): void {
  if (document.querySelector(`script[${MEDIANET_SCRIPT_ATTR}]`)) return;

  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `//contextual.media.net/dmedianet.js?cid=${siteId}`;
  script.setAttribute(MEDIANET_SCRIPT_ATTR, 'true');
  // Graceful failure: silent on error so layout is never disrupted (FR-036).
  script.onerror = () => {};
  document.head.appendChild(script);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AdSlot({ variant, slotId }: Props) {
  const { width, height } = SIZES[variant];

  // Resolve the provider on every render. In the CRA production build, CRA's
  // transform inlines this to a string literal, so it behaves as a build-time
  // constant. In Jest, process.env is mutable, so tests can control the
  // provider by setting process.env.REACT_APP_AD_PROVIDER before rendering.
  const provider = getProvider();

  // ── AdSense credentials (only read when provider === 'adsense') ──
  const publisherId =
    provider === 'adsense'
      ? (process.env.REACT_APP_AD_PUBLISHER_ID ?? '')
      : '';

  // ── Media.net credentials (only read when provider === 'medianet') ──
  const siteId =
    provider === 'medianet'
      ? (process.env.REACT_APP_MEDIANET_SITE_ID ?? '')
      : '';

  // Stable div ID for Media.net's named-div requirement.
  // Pattern: mn-slot-{variant}-{slotId} (e.g. "mn-slot-leaderboard-987654321")
  const medianetDivId =
    slotId != null ? `mn-slot-${variant}-${slotId}` : undefined;

  // Guards against double-push on React strict-mode double-invoke or re-render.
  const pushCalledRef = useRef(false);

  // ── Effect 1: inject the global loader script for the active provider ──
  useEffect(() => {
    if (provider === 'adsense') {
      if (!publisherId) return;
      loadAdsenseLoader(publisherId);
    } else if (provider === 'medianet') {
      if (!siteId) return;
      loadMedianetLoader(siteId);
    }
    // 'none' → no-op
  }, [provider, publisherId, siteId]);

  // ── Effect 2: per-slot initialization (push / loadTag) ──
  useEffect(() => {
    if (provider === 'adsense') {
      if (!publisherId || !slotId) return;
      if (pushCalledRef.current) return;
      pushCalledRef.current = true;

      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        // AdSense push failures are non-fatal — slot stays in pre-allocated state.
      }
    } else if (provider === 'medianet') {
      if (!siteId || !slotId || !medianetDivId) return;
      if (pushCalledRef.current) return;
      pushCalledRef.current = true;

      try {
        window._mNDetails.loadTag(medianetDivId, slotId, siteId);
      } catch {
        // loadTag failures are non-fatal — slot stays in pre-allocated state.
      }
    }
    // 'none' → no-op
  }, [provider, publisherId, siteId, slotId, medianetDivId]);

  // ── Render ──
  return (
    <div
      className="flex items-center justify-center bg-gray-100 border border-dashed border-gray-300 text-gray-400 text-xs mx-auto"
      style={{ width, minHeight: height, maxWidth: '100%' }}
      aria-label="Advertisement"
      role="complementary"
    >
      {/* TC-008 / FR-035: "Advertisement" must be a visible text node, not CSS-only. */}
      Advertisement

      {/* AdSense slot markup: <ins class="adsbygoogle"> */}
      {provider === 'adsense' && publisherId && slotId && (
        <ins
          className="adsbygoogle"
          style={{ display: 'block', width, minHeight: height, maxWidth: '100%' }}
          data-ad-client={publisherId}
          data-ad-slot={slotId}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      )}

      {/* Media.net slot markup: named <div> targeted by loadTag */}
      {provider === 'medianet' && siteId && slotId && medianetDivId && (
        <div id={medianetDivId} />
      )}

      {/* provider === 'none' → no slot markup (placeholder only, AC-5) */}
    </div>
  );
}
